// frontend/src/admin/v2/hooks/useJobs.js
import { useEffect, useState, useCallback, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_BASE_URL
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY
const HEADERS = ADMIN_KEY ? { 'X-Admin-Key': ADMIN_KEY } : {}

/**
 * useJobs — fetches /api/admin/jobs.
 *
 * Adaptive polling: 5s while any job is running, 30s otherwise. Lets a
 * Run-now click surface live progress within seconds without burning
 * unnecessary requests when the system is idle.
 *
 * Run-now jobs are tracked in sessionStorage so toasts fire even if the
 * user navigates away from /admin/jobs and back while the work is running.
 *
 * AbortController cancels inflight on unmount.
 *
 * Returns { data, loading, error, errorTitle, refetch, runNow, events, dismissEvent }
 *   runNow(key)        → POST /api/admin/jobs/<key>/run, then refetch.
 *   events             → array of { id, key, label, status, durationS, errorTail }
 *                        emitted when a Run-now-tracked job completes
 *   dismissEvent(id)   → remove an event from the queue
 *
 * 🔴 Welle 4 PR 1 (§4.1): `error` ist der Nutzersatz, `errorTitle` traegt das
 * Entwicklerdetail (den Env-Namen) fuer den Tooltip.
 */
const IDLE_POLL_MS = 30_000
const ACTIVE_POLL_MS = 5_000
const STORAGE_KEY = 'pog.admin.jobs.watched'

function anyRunning(payload) {
  return Boolean(payload?.jobs?.some(j => j.last_run?.status === 'running'))
}

function loadWatched() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function saveWatched(watched) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(watched))
  } catch {
    /* sessionStorage disabled / quota — silently degrade (no toasts) */
  }
}

export function useJobs() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorTitle, setErrorTitle] = useState(null)
  const [events, setEvents] = useState([])
  const inflightRef = useRef(null)
  const dataRef = useRef(null)
  const watchedRef = useRef(loadWatched())

  const detectCompletions = useCallback((payload) => {
    const watched = watchedRef.current
    if (!payload?.jobs || Object.keys(watched).length === 0) return
    let dirty = false
    for (const job of payload.jobs) {
      const w = watched[job.key]
      if (!w) continue
      const last = job.last_run
      if (!last) continue
      const finishedAt = last.finished_at ? Date.parse(last.finished_at) : null
      const isTerminal = last.status === 'done' || last.status === 'error'
      // Match: a non-running terminal run that finished AFTER we triggered it.
      if (isTerminal && finishedAt && finishedAt >= w.triggeredAt) {
        setEvents(prev => [...prev, {
          id: `${job.key}-${last.id}`,
          key: job.key,
          label: job.label,
          status: last.status,
          durationS: last.duration_s,
          errorTail: last.error_snippet || null,
        }])
        delete watched[job.key]
        dirty = true
      }
    }
    if (dirty) saveWatched(watched)
  }, [])

  const fetchAll = useCallback(async () => {
    if (inflightRef.current) inflightRef.current.abort()
    const c = new AbortController()
    inflightRef.current = c
    try {
      const r = await axios.get(`${API}/api/admin/jobs`, {
        headers: HEADERS,
        signal: c.signal,
      })
      if (inflightRef.current !== c) return
      setData(r.data)
      dataRef.current = r.data
      detectCompletions(r.data)
      setError(null)
      setErrorTitle(null)
    } catch (e) {
      if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
      const isKey = e.response?.status === 403
      setError(isKey ? 'Admin data is unavailable in this build.' : (e.message || 'Failed to load'))
      setErrorTitle(isKey ? 'REACT_APP_ADMIN_KEY' : null)
    } finally {
      if (inflightRef.current === c) {
        setLoading(false)
        inflightRef.current = null
      }
    }
  }, [detectCompletions])

  const runNow = useCallback(async (key) => {
    const r = await axios.post(`${API}/api/admin/jobs/${key}/run`, null, { headers: HEADERS })
    // Mark this job as "user-triggered" so the next completed run fires a
    // toast. Persisted to sessionStorage so navigation away/back keeps the
    // watch alive.
    watchedRef.current = { ...watchedRef.current, [key]: { triggeredAt: Date.now() } }
    saveWatched(watchedRef.current)
    // Schedule a refetch after a short delay so the new admin_sync_runs row
    // (status='running') is visible — the adaptive poll then keeps pace at 5s.
    setTimeout(fetchAll, 1200)
    return r.data
  }, [fetchAll])

  const dismissEvent = useCallback((id) => {
    setEvents(prev => prev.filter(e => e.id !== id))
  }, [])

  useEffect(() => {
    fetchAll()
    let timeoutId = null
    const tick = () => {
      const interval = anyRunning(dataRef.current) ? ACTIVE_POLL_MS : IDLE_POLL_MS
      timeoutId = setTimeout(async () => {
        await fetchAll()
        tick()
      }, interval)
    }
    tick()
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [fetchAll])

  return { data, loading, error, errorTitle, refetch: fetchAll, runNow, events, dismissEvent }
}


/**
 * useJob — fetches a single job's detail + last 50 runs.
 * Re-fetches every 15s while a run is "running" so the user sees progress.
 */
export function useJob(key) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const inflightRef = useRef(null)

  const fetchOne = useCallback(async () => {
    if (!key) return
    if (inflightRef.current) inflightRef.current.abort()
    const c = new AbortController()
    inflightRef.current = c
    try {
      const r = await axios.get(`${API}/api/admin/jobs/${key}`, {
        headers: HEADERS,
        signal: c.signal,
      })
      if (inflightRef.current !== c) return
      setData(r.data)
      setError(null)
    } catch (e) {
      if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
      setError(e.response?.status === 404
        ? 'Job not found.'
        : (e.message || 'Failed to load'))
    } finally {
      if (inflightRef.current === c) {
        setLoading(false)
        inflightRef.current = null
      }
    }
  }, [key])

  const runNow = useCallback(async () => {
    if (!key) return
    await axios.post(`${API}/api/admin/jobs/${key}/run`, null, { headers: HEADERS })
    setTimeout(fetchOne, 1500)
  }, [key, fetchOne])

  // Auto-poll every 15s while there's a running run; otherwise 60s.
  useEffect(() => {
    fetchOne()
    let id
    const isRunning = data?.last_run?.status === 'running'
    id = setInterval(fetchOne, isRunning ? 15_000 : 60_000)
    return () => {
      clearInterval(id)
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [fetchOne, data?.last_run?.status])

  return { data, loading, error, refetch: fetchOne, runNow }
}
